import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {catchError} from 'rxjs/operators';
import {TaxplanIQServices} from "../services/TaxplanIQServices";
import {SOURCE_TYPE} from "../constants/Tpiq.constants";
import {Session} from "../services/Session";

export function signupUrl() {
  // Updated from /TaxplanIqServices/signup → new HubSpot-driven firm creation endpoint
  return `/TaxPlanningServices/create-new-firm`;
}

export function getSubscriptionPlanDataUrl(customerId, planId) {
  return `/TaxplanIqServices/customers/${customerId}/plan/${planId}`;
}

export function companyDataUrl(orgId, userId) {
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/company`;
}

export function getCompanyOnboardingUrl(orgId, userId, status) {
  let url = `/TaxPlanningServices/orgs/${orgId}/users/${userId}/company/onboarding`;
  if (status) {
    url += `?status=${status}`;
  }
  return url;
}

export function subscriptionDataUrl(plan) {
  return `/TaxplanIqServices/subscriptions/${plan}`;
}

export function couponsDataUrl(coupon) {
  return `/TaxplanIqServices/coupons/${coupon}`;
}

export function meetingValidationUrl(orgId, userId) {
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/meetingStatus`;
}

export function termsUrl(orgId, userId) {
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/terms`;
}

export function acceptTermsUrl(orgId, userId) {
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/terms/accept`;
}

export function contactPrefillUrl(email: string) {
  return `/TaxPlanningServices/signup/contact-prefill?email=${encodeURIComponent(email)}`;
}

export function subscriptionPrefillUrl(subscriptionId: string) {
  return `/TaxPlanningServices/signup/subscription-prefill?subscription_id=${encodeURIComponent(subscriptionId)}`;
}

export function createFromSubscriptionUrl() {
  return `/TaxPlanningServices/signup/create-from-subscription`;
}

export function sendBackupLinkUrl() {
  return `/TaxPlanningServices/signup/send-backup-link`;
}

export function firmLogoUploadUrl(orgId: string, userId: string) {
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/document/logos`;
}

export function meetingDurationsUrl(orgId: string, userId: string) {
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/meetings/durations`;
}

export function meetingAvailabilityUrl(orgId: string, userId: string, timezone: string, monthOffset = 0, durationMs = 1800000) {
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/meetings/availability?timezone=${encodeURIComponent(timezone)}&monthOffset=${monthOffset}&duration=${durationMs}`;
}

export function meetingBookUrl(orgId: string, userId: string) {
  return `/TaxPlanningServices/orgs/${orgId}/users/${userId}/meetings/book`;
}

export interface MeetingSlot {
  startMillisUtc: number;
  endMillisUtc: number;
}

export interface MeetingConfirmation {
  start: number;
  end: number;
  displayDate: string;
  displayTime: string;
  hostName: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignupService extends TaxplanIQServices {

  options = {};

  constructor(http: HttpClient) {
    super(http);
  }

  signup(obj): Observable<any> {
    const url = signupUrl();
    return this.create(url, obj, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  saveSubscriptionPlanData(customerId, planName): Observable<any> {
    const url = getSubscriptionPlanDataUrl(customerId, planName);
    return this.update(url, {}, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  saveCompanyDetails(orgId, userId, companyData): Observable<any> {
    const url = companyDataUrl(orgId, userId);
    return this.create(url, companyData, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getCompanyDetails(orgId, userId): Observable<any> {
    const url = companyDataUrl(orgId, userId);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  updateCompanyOnboarding(orgId, userId, status): Observable<any> {
    const url = getCompanyOnboardingUrl(orgId, userId, status);
    return this.update(url, {}, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getSubscriptionData(plan): Observable<any> {
    const url = subscriptionDataUrl(plan);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getCouponData(coupon): Observable<any> {
    const url = couponsDataUrl(coupon);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  checkForMeetingConfigured(org: string, userId: string) {
    const url = meetingValidationUrl(org, userId);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getTerms(orgId: string, userId: string): Observable<any> {
    const url = termsUrl(orgId, userId);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  acceptTerms(orgId: string, userId: string, body: any): Observable<any> {
    const url = acceptTermsUrl(orgId, userId);
    return this.create(url, body, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  createFromSubscription(subscriptionId: string, formData: any): Observable<any> {
    const url = createFromSubscriptionUrl();
    const payload = {
      subscriptionId,
      firstName:       formData.firstName,
      lastName:        formData.lastName,
      email:           formData.email,
      phoneNumber:     formData.phoneNumber,
      companyName:     formData.companyName,
      websiteUrl:      formData.websiteUrl,
      desiredPassword: formData.desiredPassword,
    };
    return this.create(url, payload, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getContactPrefill(email: string): Observable<any> {
    const url = contactPrefillUrl(email);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getSubscriptionPrefill(subscriptionId: string): Observable<any> {
    const url = subscriptionPrefillUrl(subscriptionId);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  sendSignupBackupEmail(email: string, firstName: string): Observable<any> {
    const url = sendBackupLinkUrl();
    return this.create(url, { email, firstName }, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  uploadLogo(orgId: string, userId: string, file: File): Observable<any> {
    const url = this.getUrl(firmLogoUploadUrl(orgId, userId), SOURCE_TYPE.JAVA);
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('payload', JSON.stringify({ sourceType: 'firm_logo', sourceId: Date.now().toString() }));
    const headers = new HttpHeaders({ 'Authorization': 'Bearer ' + Session.getToken() });
    return this.http.post<any>(url, formData, { headers }).pipe(catchError(this.handleError));
  }

  getMeetingDurations(orgId: string, userId: string): Observable<{ availableDurations: number[]; slug: string }> {
    const url = meetingDurationsUrl(orgId, userId);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  getMeetingAvailability(orgId: string, userId: string, timezone: string, monthOffset = 0, durationMs = 1800000): Observable<{ slots: MeetingSlot[]; availableDurations?: number[] }> {
    const url = meetingAvailabilityUrl(orgId, userId, timezone, monthOffset, durationMs);
    return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  bookMeeting(orgId: string, userId: string, body: {
    firstName: string; lastName: string; email: string;
    startTime: number; timezone: string; durationMs: number;
  }): Observable<MeetingConfirmation> {
    const url = meetingBookUrl(orgId, userId);
    return this.create(url, body, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
  }

  private handleError(error: HttpErrorResponse): any {
    const body = error.error;
    const message =
      typeof body === 'string'
        ? body
        : body?.message ?? body?.error ?? error.message ?? 'Network error';
    return throwError(() => ({
      ...(body && typeof body === 'object' ? body : {}),
      error: message,
      message,
    }));
  }
}
